import { SecurityColumnVisibility } from '../../../../features/clients/client/portfolio/overview/store/types';
import { ColumnChanges } from './types';

/**
 * Registry of columns introduced at each preference version.
 * When adding a new column with a migration, add an entry here so existing
 * users are notified about the new column.
 *
 * Example:
 *   3: ['myNewField'],
 */
export const columnsIntroducedAtVersion: Record<number, (keyof SecurityColumnVisibility)[]> = {
  // Future entries go here, e.g.:
  // 3: ['myNewField'],
};

/**
 * Registry of columns removed at each preference version.
 * When removing a column with a migration, add an entry here so existing
 * users are notified about the removal.
 */
export const columnsRemovedAtVersion: Record<number, string[]> = {
  // Future entries go here, e.g.:
  // 3: ['My new field']
};

/**
 * Detects new or removed columns by comparing the user's pre-migration version
 * against the registries. Any columns introduced or removed after the user's
 * stored version are surfaced as changes.
 */
export const detectColumnChanges = (preMigrationVersion: number | undefined): ColumnChanges => {
  if (preMigrationVersion === undefined) {
    return { newColumns: [], removedColumns: [], hasChanges: false };
  }

  const newColumns: (keyof SecurityColumnVisibility)[] = [];
  const removedColumns: string[] = [];

  for (const [versionStr, cols] of Object.entries(columnsIntroducedAtVersion)) {
    const version = Number(versionStr);
    if (version > preMigrationVersion) {
      newColumns.push(...cols);
    }
  }

  for (const [versionStr, cols] of Object.entries(columnsRemovedAtVersion)) {
    const version = Number(versionStr);
    if (version > preMigrationVersion) {
      removedColumns.push(...cols);
    }
  }

  return {
    newColumns,
    removedColumns,
    hasChanges: newColumns.length > 0 || removedColumns.length > 0,
  };
};

const columnLabels: Required<Record<keyof SecurityColumnVisibility, string>> = {
  name: 'Name',
  securityCode: 'Code',
  units: 'Units',
  costBase: 'Cost Base',
  unitCost: 'Avg Unit Cost',
  unitPrice: 'Price (AUD)',
  nativePrice: 'Price (Native)',
  currentValue: 'Value (AUD)',
  nativeValue: 'Value (Native)',
  currentWeightPercentage: 'Port.%',
  gainLossPercentage: 'Unrealised Gain/Loss %',
  returnSinceInception: 'Return Since Inception',
  returnSinceInceptionPercentage: 'Return Since Inception %',
  gainLossValue: 'Unrealised Gain/Loss',
  estimatedIncome: 'Est.Income',
  estimatedYield: 'Est. Yield',
  estimatedGrossIncome: 'Est.Gross Income',
  estimatedGrossYield: 'Est.Gross Yield',
  priceDate: 'Price Date',
  currencyCode: 'Native Currency',
  forexRate: 'Forex Rate',
  targetValue: 'Target Value',
  targetWeight: 'Target Weight',
  differenceValue: 'Difference Value',
  differenceWeightPercentage: 'Difference Weight %',
  unsettledUnits: 'Unsettled Units',
  unsettledValue: 'Unsettled Value',
};

export const getColumnLabel = (key: string): string => columnLabels[key as keyof SecurityColumnVisibility] ?? key;
